import {
  balanceMatchesAccount,
  balanceEquity,
  balanceCash,
  balanceBuyingPower,
  balanceTodaysPl,
  balanceRealizedPl,
  balanceUnrealizedPl,
  balanceAccountType,
  accountNumber,
} from './canonicalFields';
import { isFuturesAccount } from './accountUtils';

/**
 * Find balance row for the selected account (canonical or legacy).
 */
export function findBalanceForAccount(balances, selectedAccount) {
  if (!Array.isArray(balances) || balances.length === 0) return null;
  if (!selectedAccount) return balances[0];
  return balances.find((b) => balanceMatchesAccount(b, selectedAccount)) || balances[0];
}

/**
 * Account value when broker omits equity (Connect Trade often returns cash/buying_power only).
 */
export function balanceDisplayEquity(balance, unrealizedPl = 0) {
  if (!balance) return null;
  const equity = parseFloat(balanceEquity(balance));
  if (Number.isFinite(equity) && equity !== 0) return equity;
  const cash = parseFloat(balanceCash(balance)) || 0;
  const upl = parseFloat(unrealizedPl) || 0;
  const total = cash + upl;
  return Number.isFinite(total) ? total : null;
}

/**
 * Balance-derived metrics for account summary cards (canonical-aware).
 */
export function getBalanceMetrics(balance, selectedAccount) {
  if (!balance || !balanceMatchesAccount(balance, selectedAccount)) {
    return {
      equity: null,
      buyingPower: null,
      cashBalance: null,
      dayPL: null,
      openPL: null,
    };
  }

  const equity = balanceDisplayEquity(balance);
  const buyingPower = parseFloat(balanceBuyingPower(balance));
  const cashBalance = parseFloat(balanceCash(balance));
  const dayPL = parseFloat(balanceTodaysPl(balance));
  const openPL = parseFloat(balanceUnrealizedPl(balance));

  return {
    equity: Number.isFinite(equity) ? equity : null,
    buyingPower: Number.isFinite(buyingPower) ? buyingPower : null,
    cashBalance: Number.isFinite(cashBalance) ? cashBalance : null,
    dayPL: Number.isFinite(dayPL) ? dayPL : null,
    openPL: Number.isFinite(openPL) ? openPL : null,
  };
}

export function buildPlDisplay({ realtimeMetrics, balanceMetrics }) {
  const equity =
    realtimeMetrics?.isReady && realtimeMetrics.accountValue > 0
      ? realtimeMetrics.accountValue
      : balanceMetrics.equity;

  if (realtimeMetrics?.isReady) {
    const openAmount = realtimeMetrics.unrealizedPL ?? 0;
    const todayAmount = realtimeMetrics.todayChange ?? 0;
    return {
      openPL: openAmount,
      openPLPercent: equity > 0 ? (openAmount / equity) * 100 : null,
      dayPL: todayAmount,
      dayPLPercent: equity > 0 ? (todayAmount / equity) * 100 : null,
      equity,
    };
  }

  const openPL = balanceMetrics.openPL ?? 0;
  const dayPL = balanceMetrics.dayPL ?? 0;
  return {
    openPL,
    openPLPercent: equity > 0 ? (openPL / equity) * 100 : null,
    dayPL,
    dayPLPercent: equity > 0 ? (dayPL / equity) * 100 : null,
    equity,
  };
}

export function plAccent(num, colors) {
  if (num > 0) return colors.chartUp;
  if (num < 0) return '#ef4444';
  return undefined;
}

export function formatPlPercent(value) {
  if (value == null || !Number.isFinite(value)) return null;
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatAccountCurrency(num) {
  if (num == null) return null;
  const n = parseFloat(num);
  if (!Number.isFinite(n)) return null;
  return currencyFmt.format(n);
}

export function accountRefForApi(selectedAccount) {
  return accountNumber(selectedAccount);
}
